import React, { useState, useEffect } from 'react';
import { dressGenerate, getFieldOptions, getUserProfile } from '../services/api';
import { useLang } from '../i18n/LanguageContext';
import { useAuth } from '../auth/AuthContext';

const fieldRows = [
  ['silhouette', 'fabric', 'color', 'neckline'],
  ['sleeve', 'sleeveStyle', 'bodice', 'waist'],
  ['length', 'skirtDetail', 'slitType', 'hem'],
];
const fieldKeys = fieldRows.flat();

const fieldIcons = {
  silhouette: 'checkroom', fabric: 'texture', color: 'palette',
  neckline: 'content_cut', sleeve: 'styler', sleeveStyle: 'styler', bodice: 'dry_cleaning',
  waist: 'straighten', length: 'straighten', skirtDetail: 'layers',
  slitType: 'design_services',
  hem: 'border_bottom',
};

const accessoryFields = [
  ['shoes', 'jewelry', 'belt', 'sunglasses'],
  ['hat', 'bag'],
];

const accessoryOptions = {
  shoes: [
    { value: 'Stiletto Heels', labelEn: 'Stiletto Heels', labelAr: 'كعب ستيليتو' },
    { value: 'Kitten Heels', labelEn: 'Kitten Heels', labelAr: 'كعب منخفض' },
    { value: 'Platform Heels', labelEn: 'Platform Heels', labelAr: 'كعب بلاتفورم' },
    { value: 'Flat Sandals', labelEn: 'Flat Sandals', labelAr: 'صنادل مسطحة' },
  ],
  jewelry: [
    { value: 'Pearl Necklace', labelEn: 'Pearl Necklace', labelAr: 'عقد لؤلؤ' },
    { value: 'Gold Chain', labelEn: 'Gold Chain', labelAr: 'سلسلة ذهبية' },
    { value: 'Crystal Earrings', labelEn: 'Crystal Earrings', labelAr: 'أقراط كريستال' },
    { value: 'Diamond Choker', labelEn: 'Diamond Choker', labelAr: 'قلادة ألماس' },
  ],
  belt: [
    { value: 'Thin Leather Belt', labelEn: 'Thin Leather Belt', labelAr: 'حزام جلد رفيع' },
    { value: 'Wide Sash Belt', labelEn: 'Wide Sash Belt', labelAr: 'حزام وشاح عريض' },
    { value: 'Chain Belt', labelEn: 'Chain Belt', labelAr: 'حزام سلسلة' },
    { value: 'Embellished Belt', labelEn: 'Embellished Belt', labelAr: 'حزام مزخرف' },
  ],
  sunglasses: [
    { value: 'Oversized Round', labelEn: 'Oversized Round', labelAr: 'دائرية كبيرة' },
    { value: 'Cat-Eye', labelEn: 'Cat-Eye', labelAr: 'عين القطة' },
    { value: 'Aviator', labelEn: 'Aviator', labelAr: 'أفياتور' },
    { value: 'Rectangular', labelEn: 'Rectangular', labelAr: 'مستطيلة' },
  ],
  hat: [
    { value: 'Fedora', labelEn: 'Fedora', labelAr: 'فيدورا' },
    { value: 'Wide Brim Sun Hat', labelEn: 'Wide Brim Sun Hat', labelAr: 'قبعة شمسية واسعة' },
    { value: 'Beret', labelEn: 'Beret', labelAr: 'بيريه' },
    { value: 'Fascinator', labelEn: 'Fascinator', labelAr: 'فاسيناتور' },
  ],
  bag: [
    { value: 'Clutch', labelEn: 'Clutch', labelAr: 'حقيبة يد صغيرة' },
    { value: 'Tote Bag', labelEn: 'Tote Bag', labelAr: 'حقيبة توت' },
    { value: 'Crossbody Bag', labelEn: 'Crossbody Bag', labelAr: 'حقيبة كروس' },
    { value: 'Mini Handbag', labelEn: 'Mini Handbag', labelAr: 'حقيبة يد صغيرة' },
  ],
};

const accessoryIcons = {
  shoes: 'steps', jewelry: 'diamond', belt: 'straighten',
  sunglasses: 'light_mode', hat: 'editor_choice', bag: 'shopping_bag',
};

export default function DressGeneratePage() {
  const { lang, t } = useLang();
  const { user, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('dress');
  const [selections, setSelections] = useState({ skirtDetail: 'None', slitType: 'None', hem: 'None', sleeveStyle: 'None' });
  const [embroideryType, setEmbroideryType] = useState('None');
  const [embroideryPositions, setEmbroideryPositions] = useState([]);
  const [accessories, setAccessories] = useState({ shoes: 'None', jewelry: 'None', belt: 'None', sunglasses: 'None', hat: 'None', bag: 'None' });
  const [generatedImage, setGeneratedImage] = useState(null);
  const [generatedImageMimeType, setGeneratedImageMimeType] = useState('image/png');
  const [outfitDetails, setOutfitDetails] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dbOptions, setDbOptions] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    getFieldOptions().then(({ data }) => setDbOptions(data)).catch(() => {});
    getUserProfile().then(({ data }) => setProfile(data)).catch(() => {});
    refreshUser();
  }, [refreshUser]);

  const isFree = (profile?.subscriptionStatus ?? user?.subscriptionStatus) === 'Free';
  const userCredits = profile?.credits ?? user?.credits ?? 0;
  const limitReached = isFree && userCredits < 2 && profile && profile.dressGenerateCount >= 2;
  const noCredits = !isFree && userCredits < 2;

  const getOptions = (fieldKey) => {
    if (dbOptions && dbOptions[fieldKey]) {
      return dbOptions[fieldKey].map(o => ({
        value: o.value,
        label: lang === 'ar' ? o.labelAr : o.labelEn,
      }));
    }
    return t.dressOptions?.[fieldKey] || [];
  };

  const getEmbroideryTypes = () => {
    if (dbOptions && dbOptions['embroideryType']) {
      return dbOptions['embroideryType'].map(o => ({
        value: o.value,
        label: lang === 'ar' ? o.labelAr : o.labelEn,
      }));
    }
    return t.embroideryTypes || [];
  };

  const getEmbroideryPositionGroups = () => {
    if (dbOptions && dbOptions['embroideryPosition']) {
      const items = dbOptions['embroideryPosition'];
      const groupMap = {};
      items.forEach(o => {
        const gKey = lang === 'ar' ? (o.groupNameAr || o.groupNameEn || 'Other') : (o.groupNameEn || 'Other');
        if (!groupMap[gKey]) groupMap[gKey] = [];
        groupMap[gKey].push({ value: o.value, label: lang === 'ar' ? o.labelAr : o.labelEn });
      });
      return Object.entries(groupMap).map(([group, positions]) => ({ group, positions }));
    }
    return t.embroideryPositionGroups || [];
  };

  const dressFields = {
    silhouette: { label: t.silhouette },
    fabric:     { label: t.fabric },
    color:      { label: t.color },
    neckline:   { label: t.neckline },
    sleeve:     { label: t.sleeves },
    sleeveStyle: { label: t.sleeveStyle },
    bodice:     { label: t.bodice },
    waist:      { label: t.waist },
    length:     { label: t.length },
    skirtDetail: { label: t.skirtDetail },
    slitType:    { label: t.slit },
    hem:         { label: t.hem },
  };

  const getFilteredOptions = (fieldKey) => {
    const allOpts = getOptions(fieldKey);
    if (fieldKey === 'sleeve') {
      const neck = selections.neckline;
      if (neck === 'Off-Shoulder' || neck === 'Halter')
        return allOpts.filter(o => o.value === 'Sleeveless');
    }
    if (fieldKey === 'sleeveStyle') {
      if (selections.sleeve === 'Sleeveless')
        return allOpts.filter(o => o.value === 'None');
    }
    if (fieldKey === 'neckline') {
      if (selections.silhouette === 'Wrap')
        return allOpts.filter(o => ['V-Neck', 'Wrap', 'Surplice'].includes(o.value));
    }
    return allOpts;
  };

  const handleChange = (key, value) => {
    setSelections(prev => {
      const next = { ...prev, [key]: value };
      if (key === 'neckline' && (value === 'Off-Shoulder' || value === 'Halter')) {
        next.sleeve = 'Sleeveless';
        next.sleeveStyle = 'None';
      }
      if (key === 'sleeve' && value === 'Sleeveless') {
        next.sleeveStyle = 'None';
      }
      if (key === 'silhouette' && value === 'Wrap') {
        if (!['V-Neck', 'Wrap', 'Surplice'].includes(prev.neckline)) next.neckline = '';
      }
      return next;
    });
  };

  const togglePosition = (posValue) => {
    setEmbroideryPositions(prev =>
      prev.includes(posValue) ? prev.filter(p => p !== posValue) : [...prev, posValue]
    );
  };

  const isEmbroiderySelected = embroideryType !== 'None';
  const allSelected = fieldKeys.every(k => selections[k]);
  const embroideryValid = !isEmbroiderySelected || embroideryPositions.length > 0;

  const handleGenerate = async () => {
    if (!allSelected) { setError(t.selectAllFirst); return; }
    if (isEmbroiderySelected && embroideryPositions.length === 0) { setError(t.selectEmbroideryPos); return; }
    setLoading(true); setError(''); setGeneratedImage(null); setOutfitDetails(null);
    setActiveTab('result');
    try {
      const payload = { ...selections, ...accessories, embroideryType, embroideryPosition: isEmbroiderySelected ? embroideryPositions.join(', ') : '' };
      const { data } = await dressGenerate(payload);
      if (data.success) {
        setGeneratedImage(data.imageBase64);
        setGeneratedImageMimeType(data.imageMimeType || 'image/png');
        setOutfitDetails(data.outfitDetails);
        setProfile(prev => prev ? { ...prev, credits: data.credits, subscriptionStatus: data.subscriptionStatus, dressGenerateCount: (prev.dressGenerateCount || 0) + 1 } : prev);
        refreshUser();
      } else { setError(data.error || 'Generation failed.'); }
    } catch (err) {
      setError(err.response?.data?.error || err.response?.data?.Error || err.message);
    }
    setLoading(false);
  };

  const detailFields = [
    { key: 'silhouette', label: t.silhouette, icon: 'checkroom' },
    { key: 'color', label: t.color, icon: 'palette' },
    { key: 'neckline', label: t.neckline, icon: 'content_cut' },
    { key: 'sleeves', label: t.sleeves, icon: 'styler' },
    { key: 'sleeveStyle', label: t.sleeveStyle, icon: 'styler' },
    { key: 'length', label: t.length, icon: 'straighten' },
    { key: 'bodice', label: t.bodice, icon: 'dry_cleaning' },
    { key: 'waist', label: t.waist, icon: 'straighten' },
    { key: 'skirtDetail', label: t.skirtDetail, icon: 'layers' },
    { key: 'embroideryType', label: t.embroidery, icon: 'draw' },
    { key: 'hem', label: t.hem, icon: 'border_bottom' },
    { key: 'embroideryPosition', label: t.embroideryPosition, icon: 'pin_drop' },
    { key: 'shoes', label: t.shoes, icon: 'steps' },
    { key: 'jewelry', label: t.jewelry, icon: 'diamond' },
    { key: 'belt', label: t.belt, icon: 'straighten' },
    { key: 'sunglasses', label: t.sunglasses, icon: 'light_mode' },
    { key: 'hat', label: t.hat, icon: 'editor_choice' },
    { key: 'bag', label: t.bag, icon: 'shopping_bag' },
  ];

  const isRtl = lang === 'ar';
  const hasResult = generatedImage || loading || error;

  // Refined styling to perfectly match the screenshot aesthetics
  const selectClasses = `custom-select block w-full rounded-md border border-border bg-white text-text py-2.5 ${isRtl ? 'pr-4 pl-10' : 'pl-4 pr-10'} text-sm focus:border-accent focus:ring-accent focus:ring-1 transition-shadow cursor-pointer hover:border-gray-300 shadow-sm`;
  const labelClasses = "flex items-center gap-1.5 text-[11px] font-semibold text-[#64748b] uppercase tracking-wide";
  const iconClasses = "material-symbols-outlined text-[16px] text-[#f59e0b]";
  const generateBtnClasses = `bg-[#FA6868] hover:bg-[#e65b5b] text-white font-semibold py-2.5 px-6 rounded-lg shadow-sm transition-all duration-200 flex items-center gap-2 text-sm ${(loading || !allSelected || !embroideryValid || limitReached || noCredits) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`;

  // Footer exact match rendering
  const renderFooter = () => (
    <div className="flex items-center justify-between mt-10 pt-6 border-t border-gray-100">
      <div className="flex items-center gap-4 text-sm font-medium">
        {profile && (
          <>
            {isFree && userCredits < 2 ? (
              <span className="text-[#64748b] flex items-center gap-1.5">
                <span className="font-medium text-gray-800">{profile.dressGenerateCount}</span> {t.of} 2 {t.generationsUsed}
              </span>
            ) : (
              <div className="flex items-center gap-1.5 text-gray-700">
                <span className="material-symbols-outlined text-[20px]" style={{ color: '#fbbf24' }}>toll</span>
                {userCredits} {t.creditsRemaining}
              </div>
            )}
            <span className="text-xs px-3 py-1 rounded-full font-semibold badge-cost">
              {t.costPerGeneration}
            </span>
          </>
        )}
      </div>
      <button onClick={handleGenerate} disabled={loading || !allSelected || !embroideryValid || limitReached || noCredits} className={generateBtnClasses}>
        <span className="material-symbols-outlined text-[18px]">
          {loading ? 'hourglass_top' : (limitReached || noCredits) ? 'lock' : 'auto_awesome'}
        </span>
        {loading ? t.generatingDress : t.generateDressBtn}
      </button>
    </div>
  );

  return (
    <div className="max-w-[1100px] w-full mx-auto px-6 py-8 pb-12" dir={isRtl ? 'rtl' : 'ltr'}>

      {/* Tabs Layout */}
      <div className="tab-bar">
        {[
          { key: 'dress', icon: 'checkroom', label: t.tabDress },
          { key: 'embroidery', icon: 'draw', label: t.tabEmbroidery },
          { key: 'accessories', icon: 'shopping_bag', label: t.tabAccessories },
          { key: 'result', icon: 'image', label: t.generatedDressTitle, disabled: !hasResult },
        ].map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`tab-item ${activeTab === tab.key ? 'active' : ''} ${tab.disabled ? 'disabled' : ''}`}
            disabled={tab.disabled}
          >
            <span className="material-symbols-outlined" style={{ fontSize: 18 }}>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Dress Options Tab ── */}
      {activeTab === 'dress' && (
        <div className="bg-surface rounded-xl shadow-soft border border-border p-8 mt-4">
          <h3 className="text-xl font-bold text-gray-800 mb-8 flex items-center gap-3">
            <span className="w-[3px] h-6 bg-[#fbbf24] rounded-full"></span>
            {t.tabDress}
          </h3>
          
          <div className="space-y-6">
            {fieldRows.map((row, rowIdx) => (
              <div key={rowIdx} className={`grid gap-x-6 gap-y-6 ${row.length === 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1 md:grid-cols-3'}`}>
                {row.map((key) => {
                  const { label } = dressFields[key];
                  const options = getFilteredOptions(key);
                  const icon = fieldIcons[key];
                  return (
                    <div key={key} className="space-y-2">
                      <label className={labelClasses}>
                        <span className={iconClasses}>{icon}</span>
                        {label}
                      </label>
                      <select
                        value={selections[key] || ''}
                        onChange={(e) => handleChange(key, e.target.value)}
                        className={selectClasses}
                      >
                        <option value="" disabled>{t.selectPlaceholder(label)}</option>
                        {options.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {error && activeTab === 'dress' && (
            <div className="mt-6 px-4 py-3 rounded-lg text-sm bg-red-50 text-red-700 border border-red-100">{error}</div>
          )}

          {renderFooter()}
        </div>
      )}

      {/* ── Embroidery Tab ── */}
      {activeTab === 'embroidery' && (
        <div className="bg-surface rounded-xl shadow-soft border border-border p-8 mt-4">
          <h3 className="text-xl font-bold text-gray-800 mb-8 flex items-center gap-3">
            <span className="w-[3px] h-6 bg-[#fbbf24] rounded-full"></span>
            {t.tabEmbroidery}
          </h3>

          <div className="space-y-2">
            <label className={labelClasses}>
              <span className={iconClasses}>draw</span>
              {t.embroideryTypeLabel}
            </label>
            <select
              value={embroideryType}
              onChange={(e) => {
                setEmbroideryType(e.target.value);
                if (e.target.value === 'None') setEmbroideryPositions([]);
              }}
              className={selectClasses}
            >
              {getEmbroideryTypes().map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          </div>

          {isEmbroiderySelected && (
            <div className="mt-6">
              <label className={labelClasses}>
                <span className={iconClasses}>pin_drop</span>
                {t.embroideryPosLabel}
              </label>
              <p className="text-xs text-[#64748b] mt-1 mb-4">{t.embroideryPosHint}</p>
              {getEmbroideryPositionGroups().map(({ group, positions }) => (
                <div key={group} className="mb-5">
                  <div className="text-xs font-bold text-[#FA6868] uppercase tracking-wide mb-2">{group}</div>
                  <div className="flex flex-wrap gap-2">
                    {positions.map((pos) => {
                      const checked = embroideryPositions.includes(pos.value);
                      return (
                        <label
                          key={pos.value}
                          onClick={() => togglePosition(pos.value)}
                          className={`inline-block px-3.5 py-1.5 rounded-lg text-sm border cursor-pointer transition-all duration-200 select-none ${
                            checked
                              ? 'bg-[#FA6868] text-white border-[#FA6868] shadow-sm'
                              : 'bg-white text-[#64748b] border-border hover:border-gray-300'
                          }`}
                        >{pos.label}</label>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          )}

          {renderFooter()}
        </div>
      )}

      {/* ── Accessories Tab ── */}
      {activeTab === 'accessories' && (
        <div className="bg-surface rounded-xl shadow-soft border border-border p-8 mt-4">
          <h3 className="text-xl font-bold text-gray-800 mb-8 flex items-center gap-3">
            <span className="w-[3px] h-6 bg-[#fbbf24] rounded-full"></span>
            {t.tabAccessories}
          </h3>
          <div className="space-y-6">
            {accessoryFields.map((row, rowIdx) => (
              <div key={rowIdx} className={`grid gap-x-6 gap-y-6 ${row.length === 4 ? 'grid-cols-2 md:grid-cols-4' : 'grid-cols-1 md:grid-cols-3'}`}>
                {row.map((key) => {
                  const label = t[key] || key;
                  const icon = accessoryIcons[key];
                  const opts = accessoryOptions[key];
                  return (
                    <div key={key} className="space-y-2">
                      <label className={labelClasses}>
                        <span className={iconClasses}>{icon}</span>
                        {label}
                      </label>
                      <select
                        value={accessories[key]}
                        onChange={(e) => setAccessories(prev => ({ ...prev, [key]: e.target.value }))}
                        className={selectClasses}
                      >
                        <option value="None">None</option>
                        {opts.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {lang === 'ar' ? opt.labelAr : opt.labelEn}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>

          {renderFooter()}
        </div>
      )}

      {/* ── Generated Image Tab ── */}
      {activeTab === 'result' && (
        <div className="space-y-6 mt-4">
          {loading && (
            <div className="bg-surface rounded-xl p-16 shadow-soft border border-border text-center">
              <div className="w-12 h-12 border-4 border-gray-200 border-t-[#FA6868] rounded-full mx-auto animate-spin"></div>
              <p className="text-[#64748b] mt-5 text-sm">{t.generatingDressLoader}</p>
            </div>
          )}

          {!loading && error && (
            <div className="bg-surface rounded-xl p-8 shadow-soft border border-border text-center">
              <span className="material-symbols-outlined text-5xl text-red-300 block mb-3">error</span>
              <p className="text-red-600 text-sm">{error}</p>
              <button
                onClick={() => setActiveTab('dress')}
                className="mt-4 px-6 py-2 rounded-lg border border-border text-sm font-medium text-[#FA6868] hover:bg-gray-50 transition-colors"
              >
                <span className="material-symbols-outlined text-[16px] mr-1 align-middle">arrow_back</span>
                Back to options
              </button>
            </div>
          )}

          {!loading && generatedImage && (
            <div className="bg-surface rounded-xl p-6 shadow-soft border border-border text-center">
              <img
                src={`data:${generatedImageMimeType};base64,${generatedImage}`}
                alt="Generated Dress"
                className="max-w-full max-h-[65vh] rounded-xl shadow-lg mx-auto object-contain"
              />
            </div>
          )}

          {!loading && outfitDetails && (
            <div className="bg-surface rounded-xl p-6 sm:p-7 shadow-soft border border-border">
              <h3 className="text-lg font-bold text-gray-800 mb-6 flex items-center gap-3">
                <span className="w-[3px] h-6 bg-[#fbbf24] rounded-full"></span>
                {t.dressDetailsTitle}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {detailFields.map(({ key, label, icon }) => {
                  const val = outfitDetails[key];
                  if (!val || val === 'None' || val === '') return null;
                  return (
                    <div key={key} className="bg-gray-50 rounded-lg px-4 py-3 border border-border">
                      <div className={labelClasses + " mb-1"}>
                        <span className="material-symbols-outlined text-[14px] text-[#FA6868]">{icon}</span>
                        {label}
                      </div>
                      <div className="text-sm text-gray-800 leading-relaxed font-medium">{val}</div>
                    </div>
                  );
                })}
              </div>
              <div className="flex justify-between items-center mt-6 pt-4 border-t border-gray-100">
                <span className="text-xs text-[#64748b]">ID: #{outfitDetails.id}</span>
                <span className="text-xs text-[#64748b]">{new Date(outfitDetails.createdAt).toLocaleString()}</span>
              </div>
            </div>
          )}

          {!loading && !generatedImage && !error && (
            <div className="bg-surface rounded-xl p-16 shadow-soft border border-border text-center">
              <span className="material-symbols-outlined text-5xl text-gray-200 block mb-3">image</span>
              <p className="text-sm text-[#64748b]">No generated image yet</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}